import { useCallback, useState } from 'react'
import type { CreateCampaignCallbacks } from '@/components/shared/types'
import type {
  CreateCampaignExperimentInput,
  CreateCampaignInput,
  OrderItem,
} from '@/flight-deck-api/services/order-items.service'
import { useOrderItemsService } from '@/flight-deck-api/services/order-items.service'
import { useAuthenticatedSession } from '@/providers/session'

export const useCreateCampaignHandler = ({
  onCreateCampaignV2,
  orderItem,
}: CreateCampaignCallbacks & { orderItem: OrderItem }) => {
  const { session } = useAuthenticatedSession()
  const [isCreating, setIsCreating] = useState(false)
  const {
    createCampaign: { handler: createCampaign },
    createCampaignExperiment: { handler: createCampaignExperiment },
  } = useOrderItemsService()

  const handleCreateCampaign = useCallback(
    async (body: CreateCampaignInput) => {
      setIsCreating(true)
      try {
        const result = await createCampaign({
          params: { 'order-item-id': orderItem.orderItemId },
          query: { 'advertiser-id': session.advertiser.id },
          body,
        })
        onCreateCampaignV2(result)
      } finally {
        setIsCreating(false)
      }
    },
    [createCampaign, onCreateCampaignV2, orderItem.orderItemId, session.advertiser.id],
  )

  const handleCreateExperiment = useCallback(
    async (body: CreateCampaignExperimentInput) => {
      setIsCreating(true)
      try {
        const result = await createCampaignExperiment({
          params: { 'order-item-id': orderItem.orderItemId },
          query: { 'advertiser-id': session.advertiser.id },
          body,
        })
        onCreateCampaignV2(result)
      } finally {
        setIsCreating(false)
      }
    },
    [createCampaignExperiment, onCreateCampaignV2, orderItem.orderItemId, session.advertiser.id],
  )

  return {
    handleCreateCampaign,
    handleCreateExperiment,
    isCreating,
  }
}
